import React, { useContext, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router';
import Web3 from 'web3';

import { useGameDetails, useHistoricRound, useKeyPress, usePriceFeedURL, useUserBetForRoundID } from 'utils/Hooks';
import { IsGivenBetEmpty } from 'utils/Hooks/useUserBetForRoundID';
import { MobileContext } from 'context';
import {
  PRICE_API_ALT_ENDPOINT, PRICE_API_DEFAULT_ENDPOINT, REWARD_PRECISION_DECIMALS, availablePositions,
  FORCED_PRICE_VERIFICATION_DELAY
} from 'const';
import RoundField from './field';
import { BackButton, unixTimestampHyperlink } from 'utils/Misc';
import HistoricPriceFeedData from './HistoricFeed/historic_feed';
import './styles.scss';


function RoundDetails(props) {
  const isMobile = useContext(MobileContext);
  const navigate = useNavigate();
  const { id: roundID } = useParams();
  const location = useLocation();

  const gameDetails = useGameDetails();
  const round = useHistoricRound(roundID);
  const userBet = useUserBetForRoundID(roundID);
  const priceFeedURL = usePriceFeedURL(true);

  const forceShowHistData = (() => {
    const showHistData = new URLSearchParams(location.search).get('showHistData');
    return (showHistData == '' || showHistData == true);
  })();

  useKeyPress(['Backspace'], () => navigate(-1));

  useEffect(() => {
    if (forceShowHistData) {
      // scroll into view after a 2s delay
      setTimeout(() => {
        document.getElementById('EndPriceField')?.scrollIntoView({ behavior: 'smooth' });
      }, FORCED_PRICE_VERIFICATION_DELAY);
    }
  }, [forceShowHistData]);


  function GetFigureInEther(figureInWei) {
    if (figureInWei === undefined) throw new SyntaxError('missing required argument: figureInWei');
    if (!figureInWei)
      return 0;
    const precisionDecimals = REWARD_PRECISION_DECIMALS[gameDetails.wantedChain.name];
    return Number(Web3.utils.fromWei(figureInWei, 'Ether')).toFixed(precisionDecimals);
  }

  function GetRewardRate() {
    if (!round || !round.rewardBaseCalAmount)
      return '-';
    else if (round.canceled || round.lockPrice == 0 || round.closePrice == 0)
      return '1.0x';
    else
      return (round.rewardAmount / round.rewardBaseCalAmount).toFixed(2) + 'x'; // net reward amount
    //return (Number(round.bullAmount) + Number(round.bearAmount)) / round.rewardBaseCalAmount; // gross reward amount
  }

  function GetWinningSide() {
    if (!round)
      return '-';
    else if (RoundNotEnded())
      return 'In progress...';
    else if (round.canceled)
      return 'Canceled';
    else if (!round.closed || round.lockPrice == 0 || round.closePrice == 0)
      return 'Round ended with an error';
    else if (round.lockPrice > round.closePrice)
      return 'Bear victory';
    else if (round.lockPrice < round.closePrice)
      return 'Bull victory';
    else if (round.lockPrice == round.closePrice)
      return 'Tied';
    else
      return 'Round ended with an error';
  }

  function GetFormattedPrice(price) {
    if (price === undefined) throw new SyntaxError('missing required argument: price');
    return (Number(price) / 10 ** 8).toFixed(gameDetails.priceDecimals) +
      ` USDT/${gameDetails.tickerSymbol}`;
  }

  function GetFormattedUserBet() {
    const betFigure = (`${Web3.utils.fromWei(userBet.amount, 'Ether')}` +
      ` ${gameDetails.wantedChain.tickerSymbol}`);
    if (userBet.position == availablePositions.bear)
      return (<React.Fragment>
        {betFigure} on <span className='BearRed'>Bear</span> (price going down)
      </React.Fragment>);
    else if (userBet.position == availablePositions.bull)
      return (<React.Fragment>
        {betFigure} on <span className='BullGreen'>Bull</span> (price going up)
      </React.Fragment>);
    else
      return '-'
  }

  function RoundNotEnded() {
    if (!round) return true;
    const roundStillInProgress = (round.closeTimestamp > (Date.now() / 1000).toFixed());
    return roundStillInProgress;
  }

  function UserBetOnThisRound() {
    return !IsGivenBetEmpty(userBet)
  }


  const priceFeedUrlM = useMemo(PriceFeedUrl, [priceFeedURL]);
  function PriceFeedUrl() {
    // Sometimes our custom hook still returns our own price mirror instead of Binance's URL
    // this makes sure that it is always correctly replaced if that happens
    let finalURL = priceFeedURL;
    if (priceFeedURL?.includes(PRICE_API_ALT_ENDPOINT)) {
      const tickerSymbol = priceFeedURL.replace(PRICE_API_ALT_ENDPOINT, '');
      finalURL = PRICE_API_DEFAULT_ENDPOINT + tickerSymbol + 'USDT'
    }
    return (<a className='longURL' href={finalURL} target='_blank' rel='noopener noreferrer'>
      {finalURL}
    </a>);
  }

  const contractURL = useMemo(ContractURL, [gameDetails]);
  function ContractURL() {
    return (<h1>All data received directly from the{' '}
      <a
        href={`${gameDetails.wantedChain.explorer}/address/${gameDetails.contracts.prediction._address}#readContract`}
        target='_blank' rel='noopener noreferrer'
      >smart contract</a>
    </h1>);
  }


  return (<div className='RoundDetailsParent'>
    <BackButton />
    <div className={'RoundDetails' + (isMobile ? ' Mobile' : ' ')}>
      {contractURL}
      <dl className='RoundFields'>
        <RoundField
          name='Round ID'
          value={roundID}
          description='Unique numeric identifier of the round used by the smart contract'
          varName='epoch'
        />
        <RoundField
          name='Bets on bull'
          value={GetFigureInEther(round.bullAmount) + ' ' + gameDetails.wantedChain.tickerSymbol}
          description={<p>
            Total amount of {gameDetails.wantedChain.tickerSymbol}{' '}
            users bet on the price of {gameDetails.tickerSymbol} going{' '}
            <b className='BullGreen'>up</b> during round #{roundID}
          </p>}
          varName='bullAmount'
        />
        <RoundField
          name='Bets on bear'
          value={GetFigureInEther(round.bearAmount) + ' ' + gameDetails.wantedChain.tickerSymbol}
          description={<p>
            Total amount of {gameDetails.wantedChain.tickerSymbol}{' '}
            users bet on the price of {gameDetails.tickerSymbol} going{' '}
            <b className='BearRed'>down</b> during round #{roundID}
          </p>}
          varName='bearAmount'
        />
        <RoundField
          name='Bets on winning side'
          value={GetFigureInEther(round.rewardBaseCalAmount) + ' ' + gameDetails.wantedChain.tickerSymbol}
          description={<p>
            Total amount of {gameDetails.wantedChain.tickerSymbol}{' '}
            users bet on the side that won. This will be the same as either{' '}
            all bets on bull or all bet on bear. If this is 0 - round is refunded
          </p>}
          varName='rewardBaseCalAmount'
        />
        <RoundField
          name='Reward pool'
          value={GetFigureInEther(round.rewardAmount) + ' ' + gameDetails.wantedChain.tickerSymbol}
          description={`Total amount of ${gameDetails.wantedChain.tickerSymbol}
          users bet on both sides <b>minus the house fee</b>`}
          varName='rewardAmount'
        />
        <RoundField
          name='Reward rate multiplier'
          value={GetRewardRate()}
          description={<p>
            How much of the original bet will be paid out to the winners.{' '}
            For example: "1x" means you just get your bet back(a refund); "2x" means you get twice{' '}
            the size of your bet; "0.5x" means you only get half your bet back.
            <br />This is a net approximate figure - it takes into account all house fee deductions
          </p>}
          varName={null}
        />

        <RoundField
          name='Bets opened timestamp'
          value={round.startTimestamp}
          description={<p>
            Time at which bets on this round opened; stored in the Unix timestamp format.{' '}
            Can be converted to human-readable date at {unixTimestampHyperlink}
          </p>}
          varName='startTimestamp'
        />
        <RoundField
          name='Bets closed timestamp'
          value={round.lockTimestamp}
          description={<p>
            Time at which bets on this round close; stored in the Unix timestamp format.{' '}
            Can be converted to human-readable date at {unixTimestampHyperlink}
          </p>}
          varName='lockTimestamp'
        />
        <RoundField
          name='End timestamp'
          value={round.closeTimestamp}
          description={<p>
            Time at which this round ends; stored in the Unix timestamp format.{' '}
            Can be converted to human-readable date at {unixTimestampHyperlink}
          </p>}
          varName='closeTimestamp'
        />

        <RoundField
          name='Start price'
          value={GetFormattedPrice(round.lockPrice)}
          description={<p>
            Price of {gameDetails.tickerSymbol} that was retrieved from the price feed{' '}
            {priceFeedUrlM} and stored on the blockchain at the{' '}
            <b>beginning</b> of this round
          </p>}
          varName='lockPrice'
          historicDataComponent={<HistoricPriceFeedData round={round}
            wantedTimestamp={round.lockPriceTimestamp}
            wantedPrice={round.lockPrice} fieldName='Start price'
          />}
        />
        <RoundField
          name='Start price timestamp'
          value={round.lockPriceTimestamp}
          description={<p>
            Time at which the above price was retrieved from the price feed{' '}
            {priceFeedUrlM} and stored on the blockchain.{' '}
            Uses Unix timestamp format.{' '}
            Can be converted to human-readable date at {unixTimestampHyperlink}
          </p>}
          varName='lockPriceTimestamp'
        />

        <RoundField id='EndPriceField'
          name='End price'
          value={GetFormattedPrice(round.closePrice)}
          description={<p>Price of {gameDetails.tickerSymbol} that was retrieved from the price feed{' '}
            {priceFeedUrlM} and stored on the blockchain at the{' '}
            <b>end</b> of this round</p>}
          varName='closePrice'
          historicDataComponent={<HistoricPriceFeedData round={round} id='EndPriceHistData'
            wantedTimestamp={round.closeTimestamp}
            wantedPrice={round.closePrice} fieldName='End price'
          />}
          forceShowHistData={forceShowHistData}
        />
        <RoundField
          name='End price timestamp'
          value={round.closePriceTimestamp}
          description={<p>
            Time at which the above price was retrieved from the price feed{' '}
            {priceFeedUrlM} and stored on the blockchain.{' '}
            Uses Unix timestamp format.{' '}
            Can be converted to human-readable date at {unixTimestampHyperlink}
          </p>}
          varName='closePriceTimestamp'
        />
        <RoundField
          name='Ended'
          value={(round.closed ? 'Yes' : 'No')}
          description='Shows whether the round has ended'
          varName='closed'
        />
        <RoundField
          name='Canceled'
          value={(round.canceled ? 'Yes' : 'No')}
          description={'Shows whether the round was canceled; if it was - ' +
            'users who bet on it can get refunds'}
          varName='canceled'
        />
        <RoundField
          name='Round result'
          value={GetWinningSide()}
          description='Shows how the round ended'
          varName={null}
        />
        {UserBetOnThisRound() ?
          <RoundField
            name='Your bet'
            value={GetFormattedUserBet()}
            description='Your bet on this round'
            varName='ignore'
          />
          :
          null
        }
      </dl>
      {contractURL}
    </div>
  </div>);
}


export default RoundDetails;
